// Devolución a proveedor (DEV) · docs/108 B6. Sale de una recepción confirmada (evidencia de origen
// obligatoria): borrador → aprobación (otra persona, según política) → CONFIRMAR, que es lo único que
// saca stock. La acreditación se DERIVA de las NC aceptadas que la imputan. Confirmada, su reversa se
// pide aquí y la aprueba otra persona en la bandeja de aprobaciones de Compras.
import { useState } from 'react';
import { PanelError } from '../comun/Estados';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { decimal, entradaDecimal, fecha } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useBodegas } from '../stock/PantallasStock';
import { DialogoMotivo, Historia, NombreProveedor } from './comun';
import {
  AvisoDoc,
  CaminoDoc,
  CuerpoDoc,
  DatosDoc,
  EncabezadoDoc,
  Tarjeta,
  type Paso,
} from './FichaDocumento';
import {
  NOMBRE_ESTADO_DEVOLUCION,
  useAccionCompras,
  useDevolucion,
  useRecepcion,
  type DevolucionLeida,
} from './datos';

export function Devolucion({
  devolucionId,
  recepcionId,
  alCreada,
  alAbrirRecepcion,
}: {
  devolucionId?: string | undefined;
  recepcionId?: string | undefined;
  alCreada: (devolucionId: string, titulo: string) => void;
  alAbrirRecepcion: (recepcionId: string, titulo: string) => void;
}) {
  const { empresa } = useSesion();
  const q = useDevolucion(empresa!.id, devolucionId ?? null);
  if (!devolucionId)
    return recepcionId ? (
      <NuevaDevolucion recepcionId={recepcionId} alCreada={alCreada} />
    ) : (
      <p className="pantalla apagado">
        Una devolución sale de una recepción confirmada: ábrela desde la recepción («Devolver al
        proveedor»).
      </p>
    );
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="apagado pantalla">Cargando devolución…</p>;
  return <FichaDevolucion d={q.data} alAbrirRecepcion={alAbrirRecepcion} />;
}

function NuevaDevolucion({
  recepcionId,
  alCreada,
}: {
  recepcionId: string;
  alCreada: (devolucionId: string, titulo: string) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const r = useRecepcion(empresaId, recepcionId);
  const accion = useAccionCompras(empresaId);
  const [cant, setCant] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  if (r.isError) return <PanelError error={r.error} />;
  if (!r.data) return <p className="apagado pantalla">Cargando recepción…</p>;
  const lineas = r.data.lineas
    .filter((l) => Number(l.cantidad_aceptada) > 0)
    .map((l) => ({ l, texto: cant[l.id] ?? '', valor: entradaDecimal(cant[l.id] ?? '', 4) }));
  const elegidas = lineas.filter((x) => x.texto.trim());
  const valido =
    motivo.trim().length >= 10 &&
    elegidas.length > 0 &&
    elegidas.every((x) => x.valor && x.valor !== '0');
  const crear = () =>
    accion.mutate(
      {
        ruta: '/compras/devoluciones',
        cuerpo: {
          recepcionId,
          motivo: motivo.trim(),
          lineas: elegidas.map((x) => ({ recepcionLineaId: x.l.id, cantidad: x.valor })),
        },
      },
      {
        onSuccess: (x) =>
          alCreada((x as { devolucionId: string }).devolucionId, 'Devolución (borrador)'),
      },
    );
  return (
    <div className="pantalla documento-compra" data-pantalla="devolucion-nueva">
      <div className="encabezado-entidad">
        <span className="ruta-migas">Compras › Devolución a proveedor</span>
        <h2>Devolver mercadería de {r.data.numeroVisible}</h2>
        <p>{r.data.recepcion?.proveedor_razon_social}</p>
      </div>
      <table className="grilla compacta" aria-label="Líneas a devolver">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Descripción</th>
            <th>Present.</th>
            <th className="der">Aceptado</th>
            <th className="der">A devolver</th>
          </tr>
        </thead>
        <tbody>
          {lineas.map(({ l, texto }) => (
            <tr key={l.id}>
              <td className="mono">{l.sku}</td>
              <td>{l.descripcion}</td>
              <td>{l.presentacion_codigo}</td>
              <td className="der">{decimal(l.cantidad_aceptada)}</td>
              <td className="der">
                <input
                  className="entrada-numero"
                  aria-label={`Devolver ${l.sku}`}
                  value={texto}
                  onChange={(e) => setCant((c) => ({ ...c, [l.id]: e.target.value }))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <label className="campo">
        <span className="etiqueta">Motivo de la devolución (mínimo 10 caracteres)</span>
        <input
          aria-label="Motivo de la devolución"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-od">
        <button
          type="button"
          className="primario"
          disabled={!valido || accion.isPending}
          onClick={crear}
        >
          Crear devolución (borrador)
        </button>
      </div>
    </div>
  );
}

const ACREDITACION: Record<string, string> = {
  ESPERANDO_NC: 'Esperando NC del proveedor',
  PARCIAL: 'Acreditada parcial',
  ACREDITADA: 'Acreditada',
};

function FichaDevolucion({
  d,
  alAbrirRecepcion,
}: {
  d: DevolucionLeida;
  alAbrirRecepcion: (recepcionId: string, titulo: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const recepcion = useRecepcion(empresaId, d.devolucion.recepcion_id);
  const accion = useAccionCompras(empresaId);
  const [dialogo, setDialogo] = useState<'aprobar' | 'rechazar' | 'cancelar' | 'reversar' | null>(
    null,
  );
  const [imprimir, setImprimir] = useState(false);
  const v = d.devolucion;
  const ruta = `/compras/devoluciones/${v.id}`;
  const titulo = d.numeroVisible ?? 'Devolución (borrador)';
  const bodega = bodegas.lista.find((b) => b.id === v.ubicacion_id);
  const lineaRecibida = new Map((recepcion.data?.lineas ?? []).map((l) => [l.id, l]));
  const reversaPendiente = d.solicitudes.some((s) => s.estado === 'PENDIENTE');
  const simple = (accionRuta: string) =>
    accion.mutate({ ruta: `${ruta}/${accionRuta}`, cuerpo: { version: v.version } });
  const e = d.estado;
  const acreditada = d.acreditacion.estado === 'ACREDITADA';
  const pasos: Paso[] = [
    {
      texto: 'Borrador',
      estado: e === 'BORRADOR' ? 'actual' : 'hecho',
      ayuda: fecha(v.fecha_negocio),
    },
    {
      texto: 'Aprobada',
      estado:
        e === 'PENDIENTE_APROBACION'
          ? 'actual'
          : e === 'APROBADA' || e === 'CONFIRMADA' || e === 'REVERSADA'
            ? 'hecho'
            : 'pendiente',
      ayuda: e === 'PENDIENTE_APROBACION' ? 'esperando la firma' : undefined,
    },
    {
      texto: 'Confirmada · sale del stock',
      estado:
        e === 'APROBADA'
          ? 'actual'
          : e === 'CONFIRMADA' || e === 'REVERSADA'
            ? 'hecho'
            : 'pendiente',
    },
    {
      texto: 'Acreditada con NC',
      estado: acreditada ? 'hecho' : e === 'CONFIRMADA' ? 'aviso' : 'pendiente',
      ayuda: e === 'CONFIRMADA' ? ACREDITACION[d.acreditacion.estado] : undefined,
    },
  ];
  const acciones = (
    <>
      {d.estado === 'BORRADOR' && puede('compras.devolucion.crear') && (
        <button
          type="button"
          className="primario"
          disabled={accion.isPending}
          onClick={() => simple('solicitar-aprobacion')}
        >
          Solicitar aprobación
        </button>
      )}
      {d.estado === 'PENDIENTE_APROBACION' && puede('compras.devolucion.aprobar') && (
        <>
          <button type="button" className="primario" onClick={() => setDialogo('aprobar')}>
            Aprobar
          </button>
          <button type="button" onClick={() => setDialogo('rechazar')}>
            Rechazar
          </button>
        </>
      )}
      {d.estado === 'APROBADA' && puede('compras.devolucion.confirmar') && (
        <button
          type="button"
          className="primario"
          disabled={accion.isPending}
          onClick={() => simple('confirmar')}
        >
          Confirmar (sale del stock)
        </button>
      )}
      {(d.estado === 'BORRADOR' ||
        d.estado === 'PENDIENTE_APROBACION' ||
        d.estado === 'APROBADA') && (
        <button type="button" onClick={() => setDialogo('cancelar')}>
          Cancelar devolución
        </button>
      )}
      {d.estado === 'CONFIRMADA' && !reversaPendiente && puede('compras.devolucion.reversar') && (
        <button type="button" onClick={() => setDialogo('reversar')}>
          Solicitar reversa
        </button>
      )}
      {d.estado === 'CONFIRMADA' && (
        <button type="button" onClick={() => setImprimir(true)}>
          Imprimir guía de devolución
        </button>
      )}
    </>
  );
  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="devolucion-ficha">
      <EncabezadoDoc
        sigla="DEV"
        migas="Compras › Devolución a proveedor"
        titulo={titulo}
        insignias={
          <>
            <span
              className={`insignia ${e === 'CONFIRMADA' ? 'tono-ok' : e === 'BORRADOR' || e === 'PENDIENTE_APROBACION' || e === 'APROBADA' ? 'tono-info' : 'tono-neutro'}`}
              data-estado={e}
            >
              {NOMBRE_ESTADO_DEVOLUCION[e]}
            </span>
            {e === 'CONFIRMADA' && (
              <span className={`insignia ${acreditada ? 'tono-ok' : 'tono-aviso'}`}>
                {ACREDITACION[d.acreditacion.estado]}
              </span>
            )}
          </>
        }
        sub={
          <>
            <b>
              <NombreProveedor id={v.proveedor_entidad_id} />
            </b>{' '}
            · {bodega ? bodega.nombre : 'bodega'} · {fecha(v.fecha_negocio)}
          </>
        }
        acciones={acciones}
      />
      <CaminoDoc pasos={pasos} />
      <AvisoDoc tono={e === 'CONFIRMADA' && !acreditada ? 'aviso' : 'info'}>
        <b>Motivo:</b> {v.motivo}
        {e === 'CONFIRMADA' && !acreditada && (
          <> · Ya salió del stock; se cierra cuando llegue la nota de crédito del proveedor.</>
        )}
      </AvisoDoc>
      {accion.isError && !dialogo && (
        <div className="doc-aviso tono-malo">
          <PanelError error={accion.error} />
        </div>
      )}
      <CuerpoDoc
        principal={
          <>
            <Tarjeta titulo="Productos devueltos" extra={`${d.lineas.length} líneas`}>
              <table className="grilla compacta" aria-label="Líneas devueltas">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>SKU</th>
                    <th>Descripción</th>
                    <th className="der">Cantidad</th>
                    <th className="der">Unid. base</th>
                  </tr>
                </thead>
                <tbody>
                  {d.lineas.map((l) => {
                    const rl = lineaRecibida.get(l.recepcion_linea_id);
                    return (
                      <tr key={l.id}>
                        <td>{l.numero}</td>
                        <td className="mono">{rl?.sku}</td>
                        <td>{rl?.descripcion}</td>
                        <td className="der">{decimal(l.cantidad)}</td>
                        <td className="der">{decimal(l.cantidad_base)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Tarjeta>
            <Tarjeta titulo="Historia" etiqueta="Historia">
              <Historia transiciones={d.transiciones} />
            </Tarjeta>
          </>
        }
        lateral={
          <Tarjeta titulo="Datos de la devolución" etiqueta="Datos de la devolución">
            <DatosDoc
              datos={[
                { e: 'Proveedor', v: <NombreProveedor id={v.proveedor_entidad_id} /> },
                {
                  e: 'Recepción',
                  v: (
                    <button
                      type="button"
                      className="enlace"
                      onClick={() =>
                        alAbrirRecepcion(
                          v.recepcion_id,
                          recepcion.data?.numeroVisible ?? 'Recepción',
                        )
                      }
                    >
                      {recepcion.data?.numeroVisible ?? 'abrir'}
                    </button>
                  ),
                },
                { e: 'Bodega', v: bodega ? `${bodega.codigo} · ${bodega.nombre}` : '…' },
                { e: 'Fecha', v: fecha(v.fecha_negocio) },
                { e: 'Devuelto (unid. base)', v: decimal(d.acreditacion.devueltaBase) },
                {
                  e: 'Acreditado (unid. base)',
                  v: decimal(d.acreditacion.acreditadaBase),
                  fuerte: true,
                },
              ]}
            />
          </Tarjeta>
        }
      />
      {dialogo && (
        <DialogoMotivo
          titulo={`${{ aprobar: 'Aprobar', rechazar: 'Rechazar', cancelar: 'Cancelar', reversar: 'Solicitar reversa de' }[dialogo]} ${titulo}`}
          minimo={dialogo === 'reversar' ? 10 : 3}
          explicacion={
            dialogo === 'reversar'
              ? 'La reversa devuelve la mercadería al stock. La aprueba OTRA persona.'
              : undefined
          }
          textoBoton={
            {
              aprobar: 'Aprobar',
              rechazar: 'Rechazar',
              cancelar: 'Cancelar devolución',
              reversar: 'Solicitar',
            }[dialogo]
          }
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={(motivo) =>
            accion.mutate(
              {
                ruta: `${ruta}/${dialogo}`,
                cuerpo: dialogo === 'reversar' ? { motivo } : { version: v.version, motivo },
              },
              { onSuccess: () => setDialogo(null) },
            )
          }
          alCerrar={() => setDialogo(null)}
        />
      )}
      {imprimir && (
        <Imprimir alTerminar={() => setImprimir(false)}>
          <article className="impreso">
            <EncabezadoImpreso
              empresa={empresa!.razon_social}
              titulo="Devolución a proveedor"
              numero={titulo}
              detalle={
                <dl className="impreso-datos">
                  <dt>Proveedor</dt>
                  <dd>{recepcion.data?.recepcion?.proveedor_razon_social}</dd>
                  <dt>Recepción</dt>
                  <dd>{recepcion.data?.numeroVisible}</dd>
                  <dt>Motivo</dt>
                  <dd>{v.motivo}</dd>
                </dl>
              }
            />
            <table className="impreso-tabla">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th className="der">Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {d.lineas.map((l) => (
                  <tr key={l.id}>
                    <td>{lineaRecibida.get(l.recepcion_linea_id)?.sku}</td>
                    <td>{lineaRecibida.get(l.recepcion_linea_id)?.descripcion}</td>
                    <td className="der">{decimal(l.cantidad)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ marginTop: '3rem' }}>
              Entregado por: ____________ Recibido por (proveedor): ____________
            </p>
          </article>
        </Imprimir>
      )}
    </div>
  );
}
